import { axios } from '@argon/app/axios'
import { objectToArray, promiseSerial, toFilterString, stampItem } from '@argon/app/helpers'
import { isEmpty } from 'lodash/lang'
import { differenceBy, last } from 'lodash/array'

const NAMESPACE = 'flows'

const getNewest = (metaItem, before, after) => {
  return differenceBy(after.meta[metaItem], before.meta[metaItem], 'name')[0]
}

// Abstract CRUD methods
const createItem = (metaItem, payload, { commit, dispatch, state }) => {
  const { flowId, data } = payload
  let url = `/flows/${flowId}/${metaItem}`
  return new Promise((resolve, reject) => {
    axios({
      url,
      data,
      method: 'POST'
    })
      .then(({ data }) => {
        return dispatch('formatFlow', data.item)
      })
      .then((flow) => {
        let latest = getNewest(metaItem, state.current, flow)
        commit('SET_CURRENT_FLOW', flow)
        resolve(latest)
      })
      .catch(reject)
  })
}
const updateItem = (metaItem, payload, { commit, dispatch }) => {
  const { flowId, data } = payload
  let url = `/flows/${flowId}/${metaItem}`
  return new Promise((resolve, reject) => {
    axios({
      url,
      data,
      method: 'PATCH'
    })
      .then(({ data }) => {
        return dispatch('formatFlow', data.item)
      })
      .then((flow) => {
        commit('SET_CURRENT_FLOW', flow)
        resolve(data)
      })
      .catch(reject)
  })
}
const deleteItem = (metaItem, payload, { commit, dispatch, state }) => {
  const { flowId, data } = payload
  let url = `/flows/${flowId}/${metaItem}`
  return new Promise((resolve, reject) => {
    axios({
      url,
      data: { name: data.name },
      method: 'DELETE'
    })
      .then(() => {
        return dispatch('getFlow', flowId)
      })
      .then(resolve)
      .catch(reject)
  })
}

const state = {
  editable: process.env.VUE_APP_ARGON_EDITABLE_FLOWS ? process.env.VUE_APP_ARGON_EDITABLE_FLOWS === 'true' : true,
  flows: [],
  flowsCount: null,
  archivedFlows: [],
  current: null,
  actionTypes: [],
  guardTypes: [],
  templates: []
}

const mutations = {
  SET_FLOWS: (state, payload) => {
    state.flows = payload
  },
  SET_FLOWS_COUNT: (state, payload) => {
    state.flowsCount = payload
  },
  SET_ARCHIVED_FLOWS: (state, payload) => {
    state.archivedFlows = payload
  },
  SET_TEMPLATES: (state, payload) => {
    state.templates = payload
  },
  SET_ACTION_TYPES: (state, payload) => {
    state.actionTypes = payload
  },
  SET_GUARD_TYPES: (state, payload) => {
    state.guardTypes = payload
  },
  SET_CURRENT_FLOW: (state, payload) => {
    state.current = payload
  }
}

const actions = {
  formatFlow: ({}, payload) => {
    return new Promise((resolve, reject) => {
      let meta = {
        ...payload.meta,
        actions: objectToArray(payload.meta.actions),
        guards: objectToArray(payload.meta.guards),
        transitions: objectToArray(payload.meta.transitions)
      }
      let flow = { ...payload, meta }
      resolve(flow)
    })
  },
  listFlows: ({ commit, rootState }, { pager }) => {
    const filters = toFilterString(rootState.app.appliedFilters[NAMESPACE])
    let params = { filters, ...pager }
    return new Promise((resolve, reject) => {
      axios({ url: '/flows', params, method: 'GET' })
        .then(({ data }) => {
          commit('SET_FLOWS', data.items)
          commit('SET_FLOWS_COUNT', data.count)
          resolve(data.items)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  listArchivedFlows: ({ commit, rootState }, payload) => {
    const filters = toFilterString(rootState.app.appliedFilters[NAMESPACE])
    let params = filters ? { include: 'only_archived', filters } : { include: 'only_archived' }
    return new Promise((resolve, reject) => {
      axios({
        url: '/flows',
        params,
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_ARCHIVED_FLOWS', data.items)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  clearFlows: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      commit('SET_FLOWS', [])
      dispatch('clearFlow')
      resolve(true)
    })
  },
  clearArchivedFlows: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_ARCHIVED_FLOWS', [])
      resolve(true)
    })
  },
  listTemplates: ({ commit }) => {
    return new Promise((resolve, reject) => {
      axios({ url: '/flow/templates', method: 'GET' })
        .then(({ data }) => {
          commit('SET_TEMPLATES', data.items)
          resolve(data.items)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  clearTemplates: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_TEMPLATES', [])
      resolve(true)
    })
  },
  getFlow: ({ commit, dispatch }, flowId) => {
    let options = {
      url: `/flows/${flowId}`,
      params: { include: 'action_types,guard_types' },
      method: 'GET'
    }
    return new Promise((resolve, reject) => {
      axios(options)
        .then(({ data }) => {
          commit('SET_ACTION_TYPES', data.action_types)
          commit('SET_GUARD_TYPES', data.guard_types)
          return dispatch('formatFlow', data)
        })
        .then((flow) => {
          commit('SET_CURRENT_FLOW', flow)
          resolve(flow)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  patchFlow: ({ dispatch }, payload) => {
    const { id, data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: `/flows/${id}`,
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          return dispatch('getFlow', id)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  archiveFlows: ({}, paths) => {
    let promises = paths.map((base) => {
      let url = `${base}/archive`
      return axios({ url, data: {}, method: 'POST' })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  restoreFlows: ({}, paths) => {
    let promises = paths.map((base) => {
      let url = `${base}/restore`
      return axios({ url, data: {}, method: 'POST' })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteFlow: ({}, flowId) => {
    let options = {
      url: `/flows/${flowId}`,
      method: 'DELETE'
    }
    return new Promise((resolve, reject) => {
      axios(options)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteFlows: ({}, paths) => {
    return new Promise((resolve, reject) => {
      let promises = paths.map((url) => axios({ url, method: 'DELETE' }))
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  clearFlow: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_CURRENT_FLOW', null)
      commit('SET_ACTION_TYPES', [])
      commit('SET_GUARD_TYPES', [])
      resolve(true)
    })
  },
  createFlow: ({ commit, state }, data) => {
    let url = '/flows'
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'POST'
      })
        .then(({ data }) => {
          commit('SET_FLOWS', [...state.flows, data])
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  createState: (context, payload) => {
    return createItem('states', payload, context)
  },
  updateState: (context, payload) => {
    return updateItem('states', payload, context)
  },
  deleteState: (context, payload) => {
    return deleteItem('states', payload, context)
  },
  createTransition: (context, payload) => {
    return createItem('transitions', payload, context)
  },
  updateTransition: (context, payload) => {
    return updateItem('transitions', payload, context)
  },
  batchUpdateTransitions: ({ commit, dispatch }, payload) => {
    const { flowId, transitions } = payload
    return new Promise((resolve, reject) => {
      if (!isEmpty(transitions)) {
        let promises = transitions.map((transition) => {
          let url = `/flows/${flowId}/transitions`
          return () =>
            axios({
              url,
              data: transition,
              method: 'PATCH'
            })
        })
        promiseSerial(promises)
          .then((responses) => {
            let lastResponse = last(responses)
            const { data } = lastResponse
            return dispatch('formatFlow', data.item)
          })
          .then((flow) => {
            commit('SET_CURRENT_FLOW', flow)
            resolve(flow)
          })
          .catch(reject)
      } else {
        resolve(true)
      }
    })
  },
  deleteTransition: (context, payload) => {
    return deleteItem('transitions', payload, context)
  },
  createGuard: (context, payload) => {
    return createItem('guards', payload, context)
  },
  updateGuard: (context, payload) => {
    return updateItem('guards', payload, context)
  },
  deleteGuard: (context, payload) => {
    return deleteItem('guards', payload, context)
  },
  createAction: (context, payload) => {
    return createItem('actions', payload, context)
  },
  updateAction: (context, payload) => {
    return updateItem('actions', payload, context)
  },
  deleteAction: (context, payload) => {
    return deleteItem('actions', payload, context)
  },
  applyDrag: ({ commit, state }, data) => {
    let meta = { ...state.current.meta }
    const { metaItem, items, dropResult } = data
    const { removedIndex, addedIndex, payload } = dropResult
    return new Promise((resolve, reject) => {
      if (removedIndex === null && addedIndex === null) {
        return resolve(items)
      }
      const result = [...items]
      let itemToAdd = payload
      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0]
      }
      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd)
      }
      meta[metaItem] = result
      let flow = { ...state.current, meta }
      commit('SET_CURRENT_FLOW', flow)
      return resolve(result)
    })
  },
  orderStates: ({}, payload) => {
    const { flowId, items } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: `/flows/${flowId}/states/order`,
        data: { order: items.map((item) => item.name) },
        method: 'POST'
      })
        .then(resolve)
        .catch(reject)
    })
  }
}

const getters = {
  items: (state) => (addonName) => state.flows[addonName] || []
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
export { NAMESPACE }
